/**
 * Туры и точечные подсветки на driver.js.
 *
 * Все цели — семантические ключи TOUR_TARGETS → `[data-tour="<key>"]`.
 * Ключи — публичный контракт: их знает чат-агент (server chat.ts, тул
 * ui_highlight) и реестр «Что нового» (adoption.ts). Селекторы за ключами
 * можно менять свободно; ключи — только добавлять.
 *
 * Шаги, чей элемент сейчас не смонтирован (например, вкладка сайдбара на
 * mobile), молча пропускаются — тур не ломается на отсутствующем DOM.
 */

import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

export const TOUR_TARGETS = [
    'topbar-title',
    'ref-selector',
    'health-badge',
    'nav-plan',
    'nav-reports',
    'nav-compare',
    'nav-print',
    'sidebar-tabs',
    'sidebar-glossary',
    'aspect-card',
    'aspect-canvas',
    'aspect-frames',
    'aspect-decisions',
    'aspect-evidence',
    'aspect-open-questions',
    'aspect-actions',
    'chat-launcher',
    'help-menu',
    'plan-plans',
    'plan-horizons',
    'plan-hypothesis',
    'plan-iterations',
    'plan-fits',
    'plan-bullseye',
    'reports-list',
    'reports-content',
    'back-to-narrative',
] as const;

export type TourTarget = (typeof TOUR_TARGETS)[number];

export interface TourStep {
    target: TourTarget | string;
    title?: string;
    text:   string;
}

function sel(target: string): string {
    return `[data-tour="${target}"]`;
}

function toDriveSteps(steps: TourStep[]): DriveStep[] {
    return steps
        .filter(s => document.querySelector(sel(s.target)) !== null)
        .map(s => ({
            element: sel(s.target),
            popover: {
                title:       s.title,
                description: s.text,
            },
        }));
}

let active: ReturnType<typeof driver> | null = null;

export function runTour(steps: TourStep[]): boolean {
    const driveSteps = toDriveSteps(steps);
    if (driveSteps.length === 0) return false;
    active?.destroy();
    active = driver({
        showProgress: true,
        nextBtnText:  'Дальше',
        prevBtnText:  'Назад',
        doneBtnText:  'Готово',
        progressText: '{{current}} из {{total}}',
        steps:        driveSteps,
    });
    active.drive();
    return true;
}

/** Точечная подсветка одного элемента (для чат-агента и hint'ов). */
export function highlightTarget(target: string, text: string, title?: string): boolean {
    if (!document.querySelector(sel(target))) return false;
    active?.destroy();
    active = driver({
        doneBtnText: 'Понятно',
    });
    active.highlight({
        element: sel(target),
        popover: { title, description: text },
    });
    return true;
}

// ─── Главный тур: «что мы решили и как к этому пришли» ────────────────────
//
// Тур не перечисляет кнопки — он ведёт по одному сценарию чтения, ради
// которого страница существует: восстановить решения команды и путь к ним.
// Шаги 1-5 идут в порядке страницы (рамки → развилки → что не решено →
// Канвасы → основания), чтобы скролл был монотонным, а не прыгал вверх-вниз;
// дальше — что с этим делать (как менялось, как дописать своё, кого спросить).
// Тот же порядок в оглавлении сайдбара — он делит аспекты по FRAME_IDS так
// же, как страница и печать. Шаг с несмонтированной целью пропускается,
// поэтому проект без Премисс/Развилок получит ту же линию, только короче.

export const WELCOME_TOUR: TourStep[] = [
    {
        target: 'topbar-title',
        title:  'Зачем эта страница',
        text:   'Здесь собран ответ на вопрос «что мы решили и почему». Модель живёт в репозитории, страница собирается из неё автоматически — и читается как разбор: сначала рамки, потом пройденные развилки, потом основания и открытые вопросы. Пройдём этот путь по шагам.',
    },
    {
        target: 'aspect-frames',
        title:  'Шаг 1. Что было решено до рынка',
        text:   'Рамки (Предпосылки) — решения, которые мы не проверяем экспериментом: «только B2B SMB», «не идём в финсектор». Они сгруппированы по силе: твёрдые не обсуждаются, «на срок» живут до даты, мягкие — низкая уверенность. Начинайте отсюда: внутри этих границ принималось всё остальное.',
    },
    {
        target: 'aspect-decisions',
        title:  'Шаг 2. Пройденные развилки',
        text:   'Ядро страницы. По каждому решению виден весь след: вопрос → что выбрали → что отвергли → почему → при каком условии вернёмся к вопросу. Свежее сверху, группы по статусу. «Отвергли» и «вернёмся, если» — то, что отличает пройденную развилку от лозунга: через полгода видно, какую ветку можно реанимировать и что должно случиться, чтобы это сделать.',
    },
    {
        target: 'aspect-open-questions',
        title:  'Шаг 3. Что ещё не решено',
        text:   'Обратная сторона предыдущего блока: открытые развилки, неясности в ситуациях, рамки с истекающим сроком, соответствия под угрозой. Стоит сразу за решениями намеренно — «закрытое» и «незакрытое» читаются парой. Это рабочий список следующих решений.',
    },
    {
        target: 'aspect-canvas',
        title:  'Шаг 4. Как решения отразились в продукте',
        text:   'Здесь начинаются Канвасы — бизнес-модель, аудитория, лестница, экономика: во что решения превратились. На сущностях висят значки-обоснования: 🔀 решение, ⚖️ рамка, 📡 сигнал. Увидели спорный блок — значок отвечает «почему он такой». Клик по имени сущности откроет карточку с исходником, связями и историей.',
    },
    {
        target: 'aspect-evidence',
        title:  'Шаг 5. На основании чего',
        text:   'После Канвасов — раздел «Поддержка»: материал, которым обосновано всё выше. Сигналы — наблюдения, интервью, метрики, разделённые на подтверждающие и опровергающие. Сюда обычно приходят не подряд, а по значку 📡 с конкретного решения — «покажи, откуда это известно».',
    },
    {
        target: 'nav-compare',
        title:  'Шаг 6. Как это менялось',
        text:   'Сравнение двух версий модели: что появилось, изменилось и исчезло между коммитами. История отдельного решения — во вкладке «История» его карточки. Вместе они дают хронологию: не только что решили, но и когда передумали.',
    },
    {
        target: 'aspect-actions',
        title:  'Шаг 7. Дописать своё',
        text:   'Ссылка на конкретный блок — чтобы обсуждать «вот это решение», а не «где-то там на странице». Соседняя кнопка заводит GitHub issue с уже подставленным контекстом: так новый вопрос попадает в очередь, а не теряется в переписке.',
    },
    {
        target: 'health-badge',
        text:   'Где след решений рвётся: пустые причины, декларации без опоры на сигналы, устаревшие данные. Красное блокирует PR-гейт — модель не примет решение без обоснования.',
    },
    {
        target: 'chat-launcher',
        title:  'Спросить, а не искать',
        text:   'Агент отвечает по модели: «почему выбрали годовую подписку?», «что мы отвергли по географии?», «что изменилось с прошлого месяца?». Может показать место в интерфейсе, оставить комментарий и завести issue — с вашего подтверждения.',
    },
    {
        target: 'sidebar-tabs',
        text:   'Навигация: «Нарративы» — оглавление, «Справочник» — все сущности по типам, «Сигналы» — лента новых наблюдений, «Вопросы» — открытые issues команды.',
    },
    {
        target: 'nav-plan',
        title:  'Что решим дальше',
        text:   'Дискавери-борд: гипотезы с порогами «да/нет», итерации, Four Fits, кольца каналов. Это будущие развилки — эксперименты, результат которых станет следующим решением на этой странице.',
    },
    {
        target: 'nav-reports',
        text:   'Журнал разборов (reports/*.md): «что изменили в модели и почему» — текстовая версия той же истории решений.',
    },
    {
        target: 'help-menu',
        text:   'Здесь всегда: руководство, гайды по DSL и методологии, повтор этого тура и «Что нового».',
    },
];

// ─── Тур по странице «План» ───────────────────────────────────────────────

export const PLAN_TOUR: TourStep[] = [
    {
        target: 'plan-plans',
        title:  'Здесь готовятся будущие решения',
        text:   'Нарратив отвечает «что мы уже решили», борд — «чем будем решать дальше». Якорь: активный План квартала и его цели — клиентские Результаты, на которые делаем ставку.',
    },
    {
        target: 'plan-horizons',
        title:  'Гипотезы по горизонтам',
        text:   '«Сейчас» — проверяем в текущей итерации, «Дальше» — на очереди, «Потом» — бэклог. Модель date-free: горизонты двигает трекер, а не календарь.',
    },
    {
        target: 'plan-hypothesis',
        title:  'Карточка гипотезы',
        text:   'Формулировка, риск по Кагану, пороги «да/нет» (числа, при которых гипотеза считается подтверждённой или опровергнутой) и связи: что проверяет, какой Показатель двигает, через какой Канал. Порог задаётся заранее — чтобы решение по итогу опиралось на число, а не на впечатление. Клик по имени — полная карточка.',
    },
    {
        target: 'plan-iterations',
        title:  'Итерации',
        text:   'Двухнедельные спринты проверки: фокус (какие гипотезы), ёмкость и перегруз, итог закрытых.',
    },
    {
        target: 'plan-fits',
        title:  'Four Fits',
        text:   'Четыре соответствия (Продукт×Рынок, Продукт×Канал, Канал×Модель, Модель×Рынок): статус каждого и гипотезы, которые его проверяют.',
    },
    {
        target: 'plan-bullseye',
        title:  'Каналы — Bullseye',
        text:   'Кольца каналов: кандидаты → тестируем → победитель/отброшен. Цвет — текущее кольцо.',
    },
    {
        target: 'back-to-narrative',
        text:   'Назад к нарративу: закрытая гипотеза становится там новой строкой в «Что решили и почему».',
    },
];

// ─── Тур по странице «Отчёты» ─────────────────────────────────────────────

export const REPORTS_TOUR: TourStep[] = [
    {
        target: 'reports-list',
        title:  'Журнал разборов',
        text:   'Файлы reports/*.md из репозитория: отчёты «что изменили в модели и почему» по датам и текстовый концепт для автора. Нарратив показывает решения в текущем состоянии, журнал — как это состояние набиралось от разбора к разбору.',
    },
    {
        target: 'reports-content',
        text:   'Выбранный отчёт целиком — можно дать ссылку заказчику или новому участнику, не открывая репозиторий.',
    },
    {
        target: 'back-to-narrative',
        text:   'Назад к нарративу — живая модель, в которой эти изменения уже учтены.',
    },
];
